import React, { useMemo, useState } from 'react';
import { Select, Empty } from 'antd';
import { isEmpty, startCase } from 'lodash';
import moment from 'moment';

import DualAxisAreaChart from '../../../../../../shared/designSystem/components/DualAxisAreaChart';
import { MenuItemTextColor } from '../../../../../../shared/globalStyling/styledText';

const { Option } = Select;

// =============================================================================
// TransactionsChart (V2 tab) — time-series chart for the unified Performance
// card. Structural parity with CampaignOverviewChart (Display tab) and the
// legacy-tab TransactionsChartV2 — head with Trend eyebrow + L/R axis
// selectors, mode-row with "By Date" / "By Day" toggle, chart body.
//
// V2 data shape differences handled here:
//   - byDate rows use `date` key for the x-axis (v1 used `xAxis`)
//   - byDayOfWeek rows use `dayOfWeek` key
//   - Metric keys: transactions / revenue / customers / spend
// We normalize to `xAxis` so the shared DualAxisAreaChart picks them up
// without special-casing.
// =============================================================================

const MODES = [
  { key: 'byDate', label: 'By Date' },
  { key: 'byDay',  label: 'By Day' },
];

// Monday-first ordering for the By Day view — parity with Display tab.
const ORDERED_DAYS = {
  Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
  Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6,
};

// Axis options — labels are the user-facing names; they map to data keys
// via DualAxisAreaChart's labelToKey (first-char lowercase + remove spaces).
// "Revenue" → "revenue" matches V2's field name.
const AXIS_OPTIONS = ['Transactions', 'Revenue', 'Customers', 'Spend'];

const TransactionsChart = ({ byDate, byDayOfWeek }) => {
  const [leftY, setLeftY] = useState('Transactions');
  const [rightY, setRightY] = useState('Revenue');
  const [mode, setMode] = useState('byDate');

  // Normalize byDate — V2 supplies `row.date` as an ISO 8601 datetime
  // (`2026-04-15T00:00:00.000Z`). Strip the time portion to a plain
  // `YYYY-MM-DD` so Recharts tick + tooltip labels show dates, not ISO
  // timestamps. Parity with the Display tab's byDate normalization.
  const byDateRows = useMemo(() => {
    if (isEmpty(byDate)) return [];
    return byDate
      .map((row) => {
        const raw = row.date || row.xAxis;
        const formatted = moment(raw).isValid()
          ? moment(raw).format('YYYY-MM-DD')
          : raw;
        return { ...row, xAxis: formatted };
      })
      .sort((a, b) => moment(a.xAxis).diff(moment(b.xAxis)));
  }, [byDate]);

  // Normalize byDayOfWeek — use `dayOfWeek` field as xAxis; sort by ISO
  // weekday order so Monday reads first.
  const byDayRows = useMemo(() => {
    if (isEmpty(byDayOfWeek)) return [];
    return byDayOfWeek
      .map((row) => ({
        ...row,
        xAxis: row.dayOfWeek || row.xAxis,
      }))
      .sort((a, b) => (ORDERED_DAYS[a.xAxis] ?? 0) - (ORDERED_DAYS[b.xAxis] ?? 0));
  }, [byDayOfWeek]);

  const activeData = mode === 'byDate' ? byDateRows : byDayRows;

  const availableModes = MODES.filter((m) => {
    if (m.key === 'byDate') return !isEmpty(byDateRows);
    if (m.key === 'byDay') return !isEmpty(byDayRows);
    return false;
  });

  if (isEmpty(activeData)) {
    return (
      <div
        className="campaign-overview__chart"
        style={{ padding: '32px 20px', display: 'flex', justifyContent: 'center' }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data yet" />
      </div>
    );
  }

  return (
    <div className="campaign-overview__chart">
      <div className="campaign-overview__chart-head">
        <div className="campaign-overview__chart-eyebrow mono-eyebrow">
          Trend
        </div>

        <div className="campaign-overview__axis-row">
          <div className="campaign-overview__axis-field">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--left" />
              Left axis
            </span>
            <Select
              onSelect={(value) => setLeftY(value)}
              className="campaign-overview__axis-select"
              value={leftY}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op !== rightY).map((option) => (
                <Option key={option} value={option}>
                  <MenuItemTextColor text={startCase(option)} color={'#7dafff'} />
                </Option>
              ))}
            </Select>
          </div>

          <div className="campaign-overview__axis-field campaign-overview__axis-field--right">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--right" />
              Right axis
            </span>
            <Select
              onSelect={(value) => setRightY(value)}
              className="campaign-overview__axis-select"
              value={rightY}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op !== leftY).map((option) => (
                <Option key={option} value={option}>
                  <MenuItemTextColor text={startCase(option)} color={'#92d5a7'} />
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {availableModes.length > 1 && (
        <div className="campaign-overview__mode-row">
          <div
            className="campaign-overview__mode segmented-tabs"
            role="tablist"
            aria-label="Chart mode"
          >
            {availableModes.map((m) => (
              <button
                key={m.key}
                role="tab"
                type="button"
                aria-selected={mode === m.key}
                onClick={() => setMode(m.key)}
                className={
                  'segmented-tabs__btn' +
                  (mode === m.key ? ' segmented-tabs__btn--active' : '')
                }
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="campaign-overview__chart-body">
        <DualAxisAreaChart
          dualChartData={activeData}
          leftY={leftY}
          rightY={rightY}
        />
      </div>
    </div>
  );
};

export default TransactionsChart;
